import clsx from 'clsx';
import { AlertCircle } from 'lucide-react';

/**
 * FormField Component
 * Wrapper for form inputs with label, error, and helper text
 * Fully accessible with proper ARIA attributes
 */

function FormField({
  label,
  htmlFor,
  error,
  helperText,
  required = false,
  children,
  className = '',
}) {
  return (
    <div className={clsx('space-y-2', className)}>
      {label && (
        <label
          htmlFor={htmlFor}
          className="block text-sm font-medium text-slate-700"
        >
          {label}
          {required && <span className="text-danger-500 ml-1" aria-label="required">*</span>}
        </label>
      )}

      {children}

      {error && (
        <div className="flex items-center gap-2 text-danger-600" role="alert">
          <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <span className="text-xs">{error}</span>
        </div>
      )}

      {!error && helperText && (
        <p className="text-xs text-slate-500">{helperText}</p>
      )}
    </div>
  );
}

/**
 * Input Component
 * Standard text input with variants
 */
export function Input({
  type = 'text',
  error,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  className = '',
  ...props
}) {
  const hasLeftIcon = LeftIcon !== undefined;
  const hasRightIcon = RightIcon !== undefined;

  return (
    <div className="relative">
      {LeftIcon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <LeftIcon className="w-5 h-5 text-slate-400" aria-hidden="true" />
        </div>
      )}

      <input
        type={type}
        className={clsx(
          'input-modern',
          error && 'input-error',
          hasLeftIcon && 'pl-10',
          hasRightIcon && 'pr-10',
          className
        )}
        aria-invalid={error ? 'true' : 'false'}
        {...props}
      />

      {RightIcon && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <RightIcon className="w-5 h-5 text-slate-400" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}

/**
 * Textarea Component
 * Multi-line text input
 */
export function Textarea({
  error,
  rows = 4,
  className = '',
  ...props
}) {
  return (
    <textarea
      rows={rows}
      className={clsx(
        'input-modern resize-y',
        error && 'input-error',
        className
      )}
      aria-invalid={error ? 'true' : 'false'}
      {...props}
    />
  );
}

/**
 * Select Component
 * Dropdown select input
 */
export function Select({
  options = [],
  error,
  placeholder = 'Select an option',
  className = '',
  ...props
}) {
  return (
    <select
      className={clsx(
        'input-modern appearance-none cursor-pointer pr-10',
        error && 'input-error',
        className
      )}
      aria-invalid={error ? 'true' : 'false'}
      {...props}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Checkbox Component
 * Checkbox input with label
 */
export function Checkbox({
  label,
  error,
  className = '',
  ...props
}) {
  return (
    <label className={clsx('flex items-center gap-3 cursor-pointer', className)}>
      <input
        type="checkbox"
        className={clsx(
          'rounded border-slate-300 text-primary-600 focus:ring-primary-500',
          error && 'border-danger-500'
        )}
        {...props}
      />
      {label && <span className="text-sm text-slate-700">{label}</span>}
    </label>
  );
}

/**
 * Radio Component
 * Radio button input with label
 */
export function Radio({
  label,
  error,
  className = '',
  ...props
}) {
  return (
    <label className={clsx('flex items-center gap-3 cursor-pointer', className)}>
      <input
        type="radio"
        className={clsx(
          'border-slate-300 text-primary-600 focus:ring-primary-500',
          error && 'border-danger-500'
        )}
        {...props}
      />
      {label && <span className="text-sm text-slate-700">{label}</span>}
    </label>
  );
}

/**
 * Toggle Component
 * Switch toggle for boolean values
 */
export function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
  className = '',
}) {
  return (
    <label className={clsx('flex items-center gap-3 cursor-pointer', disabled && 'opacity-50 cursor-not-allowed', className)}>
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="sr-only"
        />
        <div
          className={clsx(
            'w-11 h-6 rounded-full transition-colors duration-200',
            checked ? 'bg-gradient-to-r from-primary-600 to-primary-500' : 'bg-slate-300'
          )}
        />
        <div
          className={clsx(
            'absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200',
            checked && 'translate-x-5'
          )}
        />
      </div>
      {label && <span className="text-sm text-slate-700">{label}</span>}
    </label>
  );
}

// Export FormField as both named and default
export { FormField };
export default FormField;

